import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Toaster, toast } from 'sonner';
import {
  Star,
  ArrowLeft,
  CircleNotch,
  DownloadSimple,
  CaretUp,
  CaretDown,
  Target,
  ArrowUp,
  ArrowDown,
  Minus,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

import { useDrawsUpdated } from '../hooks/useDrawsUpdated';
import W3FamilyFinalVerdict from '../components/W3FamilyFinalVerdict';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const ClassBadge = ({ value }) => {
  if (value === 'UP') return <span className="inline-flex items-center gap-0.5 text-green-700 bg-green-100 rounded px-1.5 py-0.5 text-xs font-bold"><ArrowUp size={10} weight="bold" />UP</span>;
  if (value === 'DOWN') return <span className="inline-flex items-center gap-0.5 text-red-700 bg-red-100 rounded px-1.5 py-0.5 text-xs font-bold"><ArrowDown size={10} weight="bold" />DN</span>;
  return <span className="inline-flex items-center gap-0.5 text-gray-600 bg-gray-100 rounded px-1.5 py-0.5 text-xs font-bold"><Minus size={10} weight="bold" />0</span>;
};

const PatternDisplay = ({ pattern }) => {
  const parts = pattern.split(',');
  return (
    <div className="flex items-center gap-0.5">
      {parts.map((p, i) => <ClassBadge key={i} value={p} />)}
    </div>
  );
};

const ResizableHeader = ({ children, width, onResize, columnKey, onClick, className = '' }) => {
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const handleMouseDown = (e) => {
    e.preventDefault(); e.stopPropagation();
    startXRef.current = e.clientX; startWidthRef.current = width;
    const move = (ev) => { onResize(columnKey, Math.max(40, startWidthRef.current + (ev.clientX - startXRef.current))); };
    const up = () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); document.body.style.cursor = ''; document.body.style.userSelect = ''; };
    document.addEventListener('mousemove', move); document.addEventListener('mouseup', up);
    document.body.style.cursor = 'col-resize'; document.body.style.userSelect = 'none';
  };
  return (
    <th className={`relative ${className}`} style={{ width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` }} onClick={onClick}>
      <div className="flex items-center justify-center gap-1 pr-2 font-bold">{children}</div>
      <div className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-blue-500/30 group flex items-center justify-center" onMouseDown={handleMouseDown} onClick={(e) => e.stopPropagation()}>
        <div className="w-0.5 h-4 bg-gray-300 group-hover:bg-blue-500 transition-colors" />
      </div>
    </th>
  );
};

const DrawRow = ({ d, idx }) => (
  <tr className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
    <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center text-gray-500 text-xs">
      {new Date(2026, d.month - 1).toLocaleString('default', { month: 'short' })}
    </td>
    <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center text-xs">{d.year}</td>
    <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-blue-50/30">{d.ball_1_diff}</td>
    <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-blue-50/30">{d.ball_2_diff}</td>
    <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-blue-50/30">{d.ball_3_diff}</td>
    <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-blue-50/30">{d.ball_4_diff}</td>
    <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-blue-50/30">{d.ball_5_diff}</td>
    <td className="px-2 py-1.5 border-b border-gray-100"><PatternDisplay pattern={d.pattern} /></td>
    <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-green-50/50 font-bold text-xs">
      {(d.pattern_pct * 100).toFixed(1)}%
    </td>
  </tr>
);

const TotalRow = ({ label, data, extraCls = 'bg-gray-200', count }) => (
  <tr className={`${extraCls} font-bold`}>
    <td className="px-2 py-1.5 border-b border-gray-300 text-center text-xs" colSpan={2}>{label}</td>
    <td className="px-2 py-1.5 border-b border-gray-300 font-mono text-center">{data.ball_1}</td>
    <td className="px-2 py-1.5 border-b border-gray-300 font-mono text-center">{data.ball_2}</td>
    <td className="px-2 py-1.5 border-b border-gray-300 font-mono text-center">{data.ball_3}</td>
    <td className="px-2 py-1.5 border-b border-gray-300 font-mono text-center">{data.ball_4}</td>
    <td className="px-2 py-1.5 border-b border-gray-300 font-mono text-center">{data.ball_5}</td>
    <td colSpan={2} className="px-2 py-1.5 border-b border-gray-300 text-center text-xs text-gray-600">{count} draws</td>
  </tr>
);

export default function Window4TrueFalseZero() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [patternSort, setPatternSort] = useState({ key: 'times', dir: 'desc' });
  const [showAll, setShowAll] = useState(false);
  const [histPage, setHistPage] = useState(1);
  const [mainTab, setMainTab] = useState('rankings');
  const HIST_PER_PAGE = 100;
  const [myDirPattern, setMyDirPattern] = useState(() => localStorage.getItem('em_direction_pattern_c1') || '');
  const [dirApplied, setDirApplied] = useState(() => !!localStorage.getItem('em_direction_pattern_c1'));
  const [dirSet, setDirSet] = useState(1);

  const handleDirSetChange = (s) => {
    setDirSet(s);
    const saved = localStorage.getItem(`em_direction_pattern_c${s}`);
    setMyDirPattern(saved || '');
    setDirApplied(!!saved);
  };

  const [colWidths, setColWidths] = useState({ rank: 50, series: 220, times: 55, pct: 60, expGap: 55, gap: 50, overdue: 60, predScore: 65 });
  const handleResize = (key, w) => setColWidths(prev => ({ ...prev, [key]: w }));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/analysis/true-false-zero`);
      setData(res.data);
    } catch { toast.error('Failed to fetch data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useDrawsUpdated(fetchData);

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const res = await axios.get(`${API}/analysis/true-false-zero/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url; link.setAttribute('download', `true_false_zero_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      toast.success('Export completed');
    } catch { toast.error('Failed to export'); }
    finally { setExportLoading(false); }
  };

  const handlePatternSort = (key) => {
    setPatternSort(prev => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' }));
  };

  const sortedPatterns = data?.ranked_patterns ? [...data.ranked_patterns].sort((a, b) => {
    const av = a[patternSort.key] ?? 0, bv = b[patternSort.key] ?? 0;
    return patternSort.dir === 'desc' ? bv - av : av - bv;
  }) : [];
  const displayPatterns = showAll ? sortedPatterns : sortedPatterns.slice(0, 30);
  const predictedPatterns = new Set((data?.prediction || []).slice(0, 10).map(p => p.pattern));

  // Group current year draws by month
  const cyGrouped = (data?.current_year_draws || []).reduce((acc, d) => {
    if (!acc[d.month]) acc[d.month] = [];
    acc[d.month].push(d);
    return acc;
  }, {});
  const cyMonthOrder = Object.keys(cyGrouped).map(Number).sort((a, b) => b - a);

  // Historical draws with pagination
  const histDraws = data?.historical_draws || [];
  const totalHistPages = Math.ceil(histDraws.length / HIST_PER_PAGE);
  const pagedHist = histDraws.slice(0, histPage * HIST_PER_PAGE);

  // Group paged historical by year
  const histGrouped = pagedHist.reduce((acc, d) => {
    if (!acc[d.year]) acc[d.year] = [];
    acc[d.year].push(d);
    return acc;
  }, {});
  const histYearOrder = Object.keys(histGrouped).map(Number).sort((a, b) => b - a);

  const SortIcon = ({ k }) => {
    if (patternSort.key !== k) return null;
    return patternSort.dir === 'desc' ? <CaretDown size={10} /> : <CaretUp size={10} />;
  };

  const headerCls = "px-2 py-2 text-xs uppercase tracking-wider text-gray-700 cursor-pointer hover:bg-gray-200 text-center";

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-gray-500 hover:text-gray-700"><ArrowLeft size={18} /></Button>
              <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center"><Star size={24} weight="fill" className="text-amber-400" /></div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-gray-900" style={{ fontFamily: 'var(--font-heading)' }}>Window 3.3 — UP/DWN (5 Balls)</h1>
                <p className="text-xs text-gray-500">UP / DOWN / ZERO Pattern Analysis (B1-B5 only)</p>
              </div>
            </div>
            <Button onClick={handleExport} disabled={exportLoading} className="btn-secondary flex items-center gap-2" data-testid="export-btn">
              {exportLoading ? <CircleNotch size={18} className="animate-spin" /> : <DownloadSimple size={18} />}
              Export Excel
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <W3FamilyFinalVerdict activeWindow="W3.3" />
        {loading ? (
          <div className="flex items-center justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>
        ) : data ? (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white border border-gray-200 rounded p-4">
                <p className="text-xs uppercase tracking-wider font-bold text-gray-500">Total Transitions</p>
                <p className="text-2xl font-bold text-gray-900 mt-1" style={{ fontFamily: 'var(--font-heading)' }}>{data.total_transitions}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded p-4">
                <p className="text-xs uppercase tracking-wider font-bold text-gray-500">Unique Patterns</p>
                <p className="text-2xl font-bold text-gray-900 mt-1" style={{ fontFamily: 'var(--font-heading)' }}>{data.ranked_patterns?.length || 0}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded p-4 bg-blue-50 border-blue-200">
                <p className="text-xs uppercase tracking-wider font-bold text-blue-600">Current Year</p>
                <p className="text-2xl font-bold text-blue-700 mt-1" style={{ fontFamily: 'var(--font-heading)' }}>{data.current_year}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded p-4">
                <p className="text-xs uppercase tracking-wider font-bold text-gray-500">Historical Draws</p>
                <p className="text-2xl font-bold text-gray-900 mt-1" style={{ fontFamily: 'var(--font-heading)' }}>{histDraws.length}</p>
              </div>
            </div>

            {/* My Direction Pattern Prediction */}
            <div className={`rounded-lg p-5 text-white transition-all mb-6 ${dirApplied ? 'bg-gradient-to-r from-emerald-600 to-green-700 ring-2 ring-emerald-400' : 'bg-gradient-to-r from-gray-600 to-gray-700'}`} data-testid="my-prediction-direction">
              {/* C1/C2/C3 selector */}
              <div className="flex items-center gap-1.5 mb-3">
                {[1,2,3].map(s => (
                  <button key={s} onClick={() => handleDirSetChange(s)} className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${dirSet === s ? (s===1?'bg-blue-600':s===2?'bg-purple-600':'bg-teal-600')+' text-white ring-2 ring-white/40 shadow-lg' : !!localStorage.getItem(`em_direction_pattern_c${s}`) ? 'bg-white/20 text-white/90' : 'bg-white/10 text-white/50 hover:bg-white/15'}`} data-testid={`dir-set-${s}`}>C{s}</button>
                ))}
                <span className="text-[10px] text-white/40 ml-2">3 independent sets</span>
              </div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-bold text-lg">My Direction Pattern Prediction</h3>
                  <p className="text-xs text-white/70">Set your predicted UP/DOWN/ZERO pattern for B1-B5 — applies across prediction windows</p>
                </div>
                {dirApplied ? (
                  <span className="text-xs bg-emerald-400/30 px-3 py-1.5 rounded-full font-bold flex items-center gap-1.5 ring-1 ring-emerald-300">
                    <span className="w-2 h-2 rounded-full bg-emerald-300 animate-pulse"></span> APPLIED
                  </span>
                ) : (
                  <span className="text-xs bg-white/10 px-3 py-1.5 rounded-full font-bold text-white/60">NOT SET</span>
                )}
              </div>

              {/* C1-C3 overview strip */}
              <div className="flex flex-wrap gap-2 mb-3 bg-white/10 rounded-lg px-4 py-2.5">
                {[1,2,3].map(s => {
                  const val = localStorage.getItem(`em_direction_pattern_c${s}`);
                  return (
                    <div key={s} className={`flex items-center gap-1.5 text-xs ${dirSet === s ? 'ring-1 ring-white/40 rounded px-1.5 py-0.5 bg-white/10' : ''}`}>
                      <span className={`font-bold ${val ? 'text-emerald-300' : 'text-white/30'}`}>C{s}:</span>
                      {val ? (
                        <span className="font-mono font-bold text-white/90">
                          {val.split(',').map(v => v === 'UP' ? '▲' : v === 'DOWN' ? '▼' : v === 'ZERO' ? '0' : '—').join('')}
                        </span>
                      ) : (
                        <span className="text-white/30 italic">not set</span>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                {['B1','B2','B3','B4','B5'].map((b, i) => {
                  const dirs = myDirPattern ? myDirPattern.split(',') : [];
                  const val = dirs[i] || '';
                  return (
                    <div key={b} className="flex flex-col items-center gap-1">
                      <span className="text-[10px] font-bold opacity-70">{b}</span>
                      <select value={val} onChange={e => {
                        const newDirs = myDirPattern ? myDirPattern.split(',') : ['','','','',''];
                        while (newDirs.length < 5) newDirs.push('');
                        newDirs[i] = e.target.value;
                        setMyDirPattern(newDirs.join(','));
                        setDirApplied(false);
                      }} className="w-20 px-1 py-1.5 rounded text-gray-900 font-mono font-bold text-center text-xs" data-testid={`dir-select-${i}`}>
                        <option value="">-</option>
                        <option value="UP">UP</option>
                        <option value="DOWN">DOWN</option>
                        <option value="ZERO">ZERO</option>
                      </select>
                    </div>
                  );
                })}
                {/* Quick pick from top prediction */}
                {data?.top4_predictions?.[0]?.pattern && (
                  <button onClick={() => { setMyDirPattern(data.top4_predictions[0].pattern); setDirApplied(false); }}
                    className="px-2 py-1 rounded text-[10px] font-mono font-bold bg-white/15 hover:bg-white/25 transition-colors ml-2">
                    Top: {data.top4_predictions[0].pattern}
                  </button>
                )}
                <Button onClick={() => {
                  localStorage.setItem(`em_direction_pattern_c${dirSet}`, myDirPattern); setDirApplied(true); toast.success(`Direction C${dirSet} applied: ${myDirPattern}`); }}
                  className={`font-bold transition-all ${dirApplied ? 'bg-emerald-500 text-white hover:bg-emerald-400 ring-2 ring-emerald-300' : 'bg-amber-400 text-amber-900 hover:bg-amber-300'}`} data-testid="save-direction-btn">
                  {dirApplied ? 'Applied' : 'Save Constraint'}
                </Button>
                {dirApplied && (
                  <Button onClick={() => { localStorage.removeItem(`em_direction_pattern_c${dirSet}`); setDirApplied(false); setMyDirPattern(''); toast.info(`Direction C${dirSet} cleared`); }}
                    variant="ghost" className="text-white/70 hover:text-white text-xs">Clear</Button>
                )}
              </div>
            </div>

            {/* Tab selector */}
            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg mb-6">
              <button onClick={() => setMainTab('rankings')} className={`flex-1 px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === 'rankings' ? 'bg-[#002FA7] text-white' : 'text-gray-600 hover:bg-gray-200'}`} data-testid="tab-w4-rankings">
                Rankings & History
              </button>
              <button onClick={() => setMainTab('top4pred')} className={`flex-1 px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === 'top4pred' ? 'bg-[#002FA7] text-white' : 'text-gray-600 hover:bg-gray-200'}`} data-testid="tab-w4-top4pred">
                Top 4 Combo Predictions
              </button>
            </div>

            {mainTab === 'rankings' && (
            <>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {/* LEFT: Pattern Rankings */}
              <div className="bg-white border border-gray-200 rounded overflow-hidden">
                <div className="p-4 border-b border-gray-200">
                  <h3 className="font-bold text-gray-900" style={{ fontFamily: 'var(--font-heading)' }}>Pattern Rankings (Ball 1-5)</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    <span className="text-green-600 font-bold">UP</span> = positive diff | <span className="text-red-600 font-bold">DOWN</span> = negative diff | <span className="text-gray-600 font-bold">ZERO</span> = no change
                  </p>
                </div>
                <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                  <table className="w-full text-xs" style={{ tableLayout: 'fixed' }}>
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <ResizableHeader width={colWidths.rank} onResize={handleResize} columnKey="rank" onClick={() => handlePatternSort('rank')} className={headerCls}>
                          # <SortIcon k="rank" />
                        </ResizableHeader>
                        <ResizableHeader width={colWidths.series} onResize={handleResize} columnKey="series" className="px-2 py-2 text-xs uppercase tracking-wider text-gray-700 text-center">
                          Series (B1-B5)
                        </ResizableHeader>
                        <ResizableHeader width={colWidths.times} onResize={handleResize} columnKey="times" onClick={() => handlePatternSort('times')} className={headerCls}>
                          Times <SortIcon k="times" />
                        </ResizableHeader>
                        <ResizableHeader width={colWidths.pct} onResize={handleResize} columnKey="pct" onClick={() => handlePatternSort('percentage')} className={headerCls}>
                          % <SortIcon k="percentage" />
                        </ResizableHeader>
                        <ResizableHeader width={colWidths.expGap} onResize={handleResize} columnKey="expGap" onClick={() => handlePatternSort('expected_gap')} className={headerCls}>
                          Exp Gap <SortIcon k="expected_gap" />
                        </ResizableHeader>
                        <ResizableHeader width={colWidths.gap} onResize={handleResize} columnKey="gap" onClick={() => handlePatternSort('gap')} className={headerCls}>
                          Gap <SortIcon k="gap" />
                        </ResizableHeader>
                        <ResizableHeader width={colWidths.overdue} onResize={handleResize} columnKey="overdue" onClick={() => handlePatternSort('overdue_ratio')} className={headerCls}>
                          Overdue <SortIcon k="overdue_ratio" />
                        </ResizableHeader>
                        <ResizableHeader width={colWidths.predScore} onResize={handleResize} columnKey="predScore" onClick={() => handlePatternSort('pred_score')} className={headerCls}>
                          Score <SortIcon k="pred_score" />
                        </ResizableHeader>
                      </tr>
                    </thead>
                    <tbody>
                      {displayPatterns.map((p, idx) => (
                        <tr key={p.series} className={predictedPatterns.has(p.series) ? 'bg-blue-100' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} style={predictedPatterns.has(p.series) ? {borderLeft: '4px solid #2563eb', backgroundColor: 'rgba(37, 99, 235, 0.18)'} : {}}>
                          <td className="px-2 py-1.5 border-b border-gray-100 font-mono font-bold text-center">{p.rank}</td>
                          <td className="px-2 py-1.5 border-b border-gray-100"><PatternDisplay pattern={p.series} /></td>
                          <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center font-bold">{p.times}</td>
                          <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center">{(p.percentage * 100).toFixed(2)}%</td>
                          <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center text-gray-500">{p.expected_gap ?? '—'}</td>
                          <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center font-bold">{p.gap ?? '—'}</td>
                          <td className={`px-2 py-1.5 border-b border-gray-100 font-mono text-center font-bold ${p.overdue_ratio > 2 ? 'text-red-600 bg-red-50' : p.overdue_ratio > 1 ? 'text-orange-600 bg-orange-50' : 'text-gray-400'}`}>
                            {p.overdue_ratio != null ? `${p.overdue_ratio}x` : '—'}
                          </td>
                          <td className={`px-2 py-1.5 border-b border-gray-100 font-mono text-center font-bold ${p.pred_score >= 10 ? 'text-red-700 bg-red-100' : p.pred_score >= 5 ? 'text-orange-700 bg-orange-50' : 'text-gray-500'}`}>
                            {p.pred_score ?? '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {sortedPatterns.length > 30 && (
                  <div className="p-3 border-t border-gray-200 text-center">
                    <Button variant="ghost" size="sm" onClick={() => setShowAll(!showAll)} className="text-xs text-blue-600">
                      {showAll ? 'Show Top 30' : `Show All ${sortedPatterns.length} Patterns`}
                    </Button>
                  </div>
                )}
              </div>

              {/* RIGHT: Draws with Patterns */}
              <div className="bg-white border border-gray-200 rounded overflow-hidden">
                <div className="p-4 border-b border-gray-200">
                  <h3 className="font-bold text-gray-900" style={{ fontFamily: 'var(--font-heading)' }}>All Draws with Patterns</h3>
                  <p className="text-xs text-gray-500 mt-1">Ball 1-5 differences + UP/DOWN/ZERO pattern + historical probability</p>
                </div>
                <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                  <table className="w-full text-xs" style={{ minWidth: '580px' }}>
                    <thead className="bg-gray-100 sticky top-0 z-[2]">
                      <tr>
                        <th className="px-2 py-2 text-xs uppercase tracking-wider text-gray-700 font-bold text-center w-[45px]">Mon</th>
                        <th className="px-2 py-2 text-xs uppercase tracking-wider text-gray-700 font-bold text-center w-[45px]">Yr</th>
                        <th className="px-2 py-2 text-xs uppercase tracking-wider text-gray-700 font-bold text-center bg-blue-100 w-[40px]">B1</th>
                        <th className="px-2 py-2 text-xs uppercase tracking-wider text-gray-700 font-bold text-center bg-blue-100 w-[40px]">B2</th>
                        <th className="px-2 py-2 text-xs uppercase tracking-wider text-gray-700 font-bold text-center bg-blue-100 w-[40px]">B3</th>
                        <th className="px-2 py-2 text-xs uppercase tracking-wider text-gray-700 font-bold text-center bg-blue-100 w-[40px]">B4</th>
                        <th className="px-2 py-2 text-xs uppercase tracking-wider text-gray-700 font-bold text-center bg-blue-100 w-[40px]">B5</th>
                        <th className="px-2 py-2 text-xs uppercase tracking-wider text-gray-700 font-bold text-center bg-purple-100" style={{ minWidth: '160px' }}>Pattern</th>
                        <th className="px-2 py-2 text-xs uppercase tracking-wider text-gray-700 font-bold text-center bg-green-100 w-[55px]">Prob</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* G.Total on top */}
                      {data.grand_total && (
                        <tr className="bg-gray-800 text-white font-bold">
                          <td className="px-2 py-2 text-center text-xs" colSpan={2}>G.Total {data.current_year}</td>
                          <td className="px-2 py-2 font-mono text-center">{data.grand_total.ball_1}</td>
                          <td className="px-2 py-2 font-mono text-center">{data.grand_total.ball_2}</td>
                          <td className="px-2 py-2 font-mono text-center">{data.grand_total.ball_3}</td>
                          <td className="px-2 py-2 font-mono text-center">{data.grand_total.ball_4}</td>
                          <td className="px-2 py-2 font-mono text-center">{data.grand_total.ball_5}</td>
                          <td colSpan={2} className="px-2 py-2 text-center text-xs">{data.grand_total.count} draws</td>
                        </tr>
                      )}

                      {/* Current year draws grouped by month */}
                      {cyMonthOrder.map(m => {
                        const draws = cyGrouped[m] || [];
                        const mt = data.monthly_totals?.find(t => t.month === m);
                        return (
                          <React.Fragment key={`cy-${m}`}>
                            {draws.map((d, idx) => <DrawRow key={d.draw_number} d={d} idx={idx} />)}
                            {mt && <TotalRow label={`Total ${mt.month_name}`} data={mt} count={mt.count} />}
                          </React.Fragment>
                        );
                      })}

                      {/* Separator */}
                      <tr className="bg-[#002FA7]">
                        <td colSpan={9} className="px-3 py-2 text-white font-bold text-xs text-center">
                          Historical Data (Before {data.current_year})
                        </td>
                      </tr>

                      {/* Historical draws grouped by year */}
                      {histYearOrder.map(yr => {
                        const draws = histGrouped[yr] || [];
                        const yt = data.yearly_totals?.find(t => t.year === yr);
                        return (
                          <React.Fragment key={`hist-${yr}`}>
                            {/* Year header */}
                            <tr className="bg-amber-100">
                              <td colSpan={9} className="px-3 py-1.5 font-bold text-xs text-amber-900">
                                {yr} ({draws.length} transitions shown{yt ? `, ${yt.count} total` : ''})
                              </td>
                            </tr>
                            {draws.map((d, idx) => <DrawRow key={d.draw_number} d={d} idx={idx} />)}
                            {yt && <TotalRow label={`Total ${yr}`} data={yt} count={yt.count} extraCls="bg-amber-200" />}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {histPage < totalHistPages && (
                  <div className="p-3 border-t border-gray-200 text-center">
                    <Button variant="ghost" size="sm" onClick={() => setHistPage(p => p + 1)} className="text-xs text-blue-600">
                      Load More Historical ({histPage * HIST_PER_PAGE} / {histDraws.length} shown)
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Transition-Based Prediction */}
            {data.last_pattern && data.prediction?.length > 0 && (
              <div className="bg-gradient-to-r from-indigo-600 to-blue-700 rounded-lg p-5 mt-6 text-white">
                <div className="flex items-center gap-2 mb-2">
                  <Target size={24} weight="bold" />
                  <h3 className="font-bold text-xl" style={{ fontFamily: 'var(--font-heading)' }}>
                    Next Pattern Prediction
                  </h3>
                </div>
                <div className="mb-4">
                  <p className="text-sm text-white/80">
                    Last draw pattern: <span className="inline-flex ml-1"><PatternDisplay pattern={data.last_pattern} /></span>
                  </p>
                  <p className="text-xs text-white/60 mt-1">
                    Score = Transition frequency after this pattern (40%) + Overall frequency (20%) + Overall rank (10%) + Overdue gap (30%)
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-white/70 border-b border-white/20">
                        <th className="px-2 py-2 text-left font-bold">#</th>
                        <th className="px-2 py-2 text-left font-bold" style={{minWidth:'160px'}}>Next Pattern</th>
                        <th className="px-2 py-2 text-center font-bold">After Last</th>
                        <th className="px-2 py-2 text-center font-bold">Follow %</th>
                        <th className="px-2 py-2 text-center font-bold">Overall Rank</th>
                        <th className="px-2 py-2 text-center font-bold">Overall %</th>
                        <th className="px-2 py-2 text-center font-bold">Draws Since</th>
                        <th className="px-2 py-2 text-center font-bold">Exp. Gap</th>
                        <th className="px-2 py-2 text-center font-bold">Overdue</th>
                        <th className="px-2 py-2 text-center font-bold">Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.prediction.slice(0, 10).map((p, idx) => (
                        <tr key={p.pattern} className={idx === 0 ? 'bg-white/20' : idx % 2 === 0 ? 'bg-white/5' : ''}>
                          <td className="px-2 py-2 font-bold">{idx + 1}</td>
                          <td className="px-2 py-2"><PatternDisplay pattern={p.pattern} /></td>
                          <td className="px-2 py-2 text-center font-mono font-bold">{p.follow_count}/{p.total_follows}</td>
                          <td className="px-2 py-2 text-center font-mono font-bold">{(p.follow_pct * 100).toFixed(1)}%</td>
                          <td className="px-2 py-2 text-center font-mono">#{p.overall_rank}</td>
                          <td className="px-2 py-2 text-center font-mono">{(p.overall_pct * 100).toFixed(1)}%</td>
                          <td className="px-2 py-2 text-center font-mono">{p.draws_since_last}</td>
                          <td className="px-2 py-2 text-center font-mono">{p.expected_gap}</td>
                          <td className={`px-2 py-2 text-center font-mono font-bold ${p.overdue_ratio >= 1.5 ? 'text-yellow-300' : ''}`}>
                            {p.overdue_ratio}x
                          </td>
                          <td className="px-2 py-2 text-center font-bold text-lg">{p.score}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {data.prediction[0] && (
                  <div className="mt-4 bg-sky-100 text-sky-900 rounded-lg p-3 flex items-center gap-3">
                    <div className="text-xs font-bold px-2 py-1 bg-yellow-900 text-yellow-400 rounded">TOP PICK</div>
                    <PatternDisplay pattern={data.prediction[0].pattern} />
                    <span className="font-bold">
                      {data.prediction[0].follow_count} times after current ({(data.prediction[0].follow_pct * 100).toFixed(1)}%) |
                      Overall #{data.prediction[0].overall_rank} |
                      {data.prediction[0].overdue_ratio >= 1 ? ` Overdue ${data.prediction[0].overdue_ratio}x` : ' Recently seen'}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Analysis Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="bg-white border border-gray-200 rounded p-4">
                <h4 className="font-bold text-gray-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                  Last Draw Pattern
                </h4>
                {data.current_year_draws?.[0] && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <PatternDisplay pattern={data.current_year_draws[0].pattern} />
                    </div>
                    <p className="text-xs text-gray-500">
                      Rank #{data.current_year_draws[0].pattern_rank} | {(data.current_year_draws[0].pattern_pct * 100).toFixed(1)}% overall
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      B1:{data.current_year_draws[0].ball_1_diff} B2:{data.current_year_draws[0].ball_2_diff} B3:{data.current_year_draws[0].ball_3_diff} B4:{data.current_year_draws[0].ball_4_diff} B5:{data.current_year_draws[0].ball_5_diff}
                    </p>
                  </div>
                )}
              </div>

              <div className="bg-white border border-gray-200 rounded p-4">
                <h4 className="font-bold text-gray-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                  Most Common in {data.current_year}
                </h4>
                <div className="space-y-1.5 max-h-[180px] overflow-y-auto">
                  {(() => {
                    const freq = {};
                    (data.current_year_draws || []).forEach(d => { freq[d.pattern] = (freq[d.pattern] || 0) + 1; });
                    return Object.entries(freq).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([pat, count]) => (
                      <div key={pat} className="flex items-center justify-between bg-indigo-50 rounded px-3 py-1.5">
                        <PatternDisplay pattern={pat} /><span className="text-sm text-indigo-700 font-bold ml-2">{count}x</span>
                      </div>
                    ));
                  })()}
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded p-4">
                <h4 className="font-bold text-gray-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                  Not Yet Seen in {data.current_year}
                </h4>
                <p className="text-xs text-gray-500 mb-2">High-probability patterns missing in {data.current_year}</p>
                <div className="space-y-1.5 max-h-[180px] overflow-y-auto">
                  {(() => {
                    const seen = new Set((data.current_year_draws || []).map(d => d.pattern));
                    return (data.ranked_patterns || []).filter(p => !seen.has(p.series)).slice(0, 5).map(p => (
                      <div key={p.series} className="flex items-center justify-between bg-orange-50 rounded px-3 py-1.5">
                        <PatternDisplay pattern={p.series} /><span className="text-sm text-orange-700 font-bold ml-2">{(p.percentage * 100).toFixed(1)}%</span>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>
            </>
            )}

            {/* Top 4 Combo Predictions Tab */}
            {mainTab === 'top4pred' && data.top4_predictions && (() => {
              const ballLabels = { ball_1: 'B1', ball_2: 'B2', ball_3: 'B3', ball_4: 'B4', ball_5: 'B5' };
              const allKeysW4 = ['ball_1','ball_2','ball_3','ball_4','ball_5'];
              return (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-[#002FA7] to-indigo-800 rounded-xl p-5 text-white">
                    <div className="flex items-center gap-2 mb-2">
                      <Target size={24} weight="bold" />
                      <h2 className="text-xl font-bold">Top 4 Combo Number Predictions (Balls 1-5)</h2>
                    </div>
                    <p className="text-sm text-blue-200">
                      Based on Prediction Score (Frequency x Overdue). Each combo tells the direction (UP/DOWN) per ball position.
                    </p>
                    <p className="text-xs text-blue-300 mt-1">
                      Last values: {allKeysW4.map(bk => `${ballLabels[bk]}=${data.last_values?.[bk]}`).join(' | ')}
                    </p>
                  </div>

                  {/* Direction Count Per Ball */}
                  {data.ball_dir_2026 && data.ball_dir_2025 && (
                    <div className="bg-white border rounded-lg overflow-hidden">
                      <div className="p-3 border-b bg-gray-50">
                        <h3 className="font-bold text-gray-900">Direction Count Per Ball (Balls 1-5)</h3>
                        <p className="text-xs text-gray-500">How many times each ball went UP, DOWN, or stayed EQUAL</p>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-3 py-2 text-left text-[10px] uppercase" rowSpan={2}>Ball</th>
                              <th className="px-3 py-2 text-center text-[10px] uppercase bg-blue-50 border-b border-blue-200" colSpan={3}>Till 2025 ({data.ball_dir_2025?.ball_1?.total || 0} draws)</th>
                              <th className="px-3 py-2 text-center text-[10px] uppercase bg-amber-50 border-b border-amber-200" colSpan={3}>2026 ({data.ball_dir_2026?.ball_1?.total || 0} draws)</th>
                              <th className="px-3 py-2 text-center text-[10px] uppercase" rowSpan={2}>Dominant</th>
                            </tr>
                            <tr>
                              <th className="px-2 py-1.5 text-center text-[10px] text-emerald-700 bg-blue-50">UP</th>
                              <th className="px-2 py-1.5 text-center text-[10px] text-red-700 bg-blue-50">DN</th>
                              <th className="px-2 py-1.5 text-center text-[10px] text-gray-600 bg-blue-50">=</th>
                              <th className="px-2 py-1.5 text-center text-[10px] text-emerald-700 bg-amber-50">UP</th>
                              <th className="px-2 py-1.5 text-center text-[10px] text-red-700 bg-amber-50">DN</th>
                              <th className="px-2 py-1.5 text-center text-[10px] text-gray-600 bg-amber-50">=</th>
                            </tr>
                          </thead>
                          <tbody>
                            {allKeysW4.map((bk) => {
                              const bd = data.ball_dir_2026[bk];
                              const bh = data.ball_dir_2025?.[bk];
                              if (!bd) return null;
                              const dominant = bd.up > bd.down ? 'UP' : bd.down > bd.up ? 'DOWN' : 'EQUAL';
                              return (
                                <tr key={bk} className="border-b">
                                  <td className="px-3 py-2 font-bold">{ballLabels[bk]}</td>
                                  <td className="px-2 py-2 text-center font-mono bg-blue-50/30">{bh?.up || 0} <span className="text-gray-400">({bh?.up_pct || 0}%)</span></td>
                                  <td className="px-2 py-2 text-center font-mono bg-blue-50/30">{bh?.down || 0} <span className="text-gray-400">({bh?.down_pct || 0}%)</span></td>
                                  <td className="px-2 py-2 text-center font-mono bg-blue-50/30">{bh?.equal || 0} <span className="text-gray-400">({bh?.equal_pct || 0}%)</span></td>
                                  <td className="px-2 py-2 text-center font-mono font-bold text-emerald-700 bg-amber-50/30">{bd.up} <span className="text-gray-400 font-normal">({bd.up_pct}%)</span></td>
                                  <td className="px-2 py-2 text-center font-mono font-bold text-red-600 bg-amber-50/30">{bd.down} <span className="text-gray-400 font-normal">({bd.down_pct}%)</span></td>
                                  <td className="px-2 py-2 text-center font-mono font-bold text-gray-600 bg-amber-50/30">{bd.equal} <span className="text-gray-400 font-normal">({bd.equal_pct}%)</span></td>
                                  <td className="px-3 py-2 text-center">
                                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${dominant === 'UP' ? 'bg-emerald-100 text-emerald-700' : dominant === 'DOWN' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>{dominant}</span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                            {(() => {
                              const t25up = allKeysW4.reduce((s, bk) => s + (data.ball_dir_2025?.[bk]?.up || 0), 0);
                              const t25dn = allKeysW4.reduce((s, bk) => s + (data.ball_dir_2025?.[bk]?.down || 0), 0);
                              const t25eq = allKeysW4.reduce((s, bk) => s + (data.ball_dir_2025?.[bk]?.equal || 0), 0);
                              const t25all = t25up + t25dn + t25eq || 1;
                              const t26up = allKeysW4.reduce((s, bk) => s + (data.ball_dir_2026[bk]?.up || 0), 0);
                              const t26dn = allKeysW4.reduce((s, bk) => s + (data.ball_dir_2026[bk]?.down || 0), 0);
                              const t26eq = allKeysW4.reduce((s, bk) => s + (data.ball_dir_2026[bk]?.equal || 0), 0);
                              const t26all = t26up + t26dn + t26eq || 1;
                              return (
                                <tr>
                                  <td className="px-3 py-2 font-bold text-gray-700">Total</td>
                                  <td className="px-2 py-2 text-center font-mono font-bold text-emerald-700 bg-blue-50/30">{t25up} <span className="text-gray-400 font-normal">({(t25up/t25all*100).toFixed(1)}%)</span></td>
                                  <td className="px-2 py-2 text-center font-mono font-bold text-red-600 bg-blue-50/30">{t25dn} <span className="text-gray-400 font-normal">({(t25dn/t25all*100).toFixed(1)}%)</span></td>
                                  <td className="px-2 py-2 text-center font-mono font-bold text-gray-600 bg-blue-50/30">{t25eq} <span className="text-gray-400 font-normal">({(t25eq/t25all*100).toFixed(1)}%)</span></td>
                                  <td className="px-2 py-2 text-center font-mono font-bold text-emerald-700 bg-amber-50/30">{t26up} <span className="text-gray-400 font-normal">({(t26up/t26all*100).toFixed(1)}%)</span></td>
                                  <td className="px-2 py-2 text-center font-mono font-bold text-red-600 bg-amber-50/30">{t26dn} <span className="text-gray-400 font-normal">({(t26dn/t26all*100).toFixed(1)}%)</span></td>
                                  <td className="px-2 py-2 text-center font-mono font-bold text-gray-600 bg-amber-50/30">{t26eq} <span className="text-gray-400 font-normal">({(t26eq/t26all*100).toFixed(1)}%)</span></td>
                                  <td className="px-3 py-2"></td>
                                </tr>
                              );
                            })()}
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Top 4 Predicted Lines */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {data.top4_predictions.map((combo, ci) => (
                      <div key={combo.pattern} className={`bg-white border rounded-lg overflow-hidden ${ci === 0 ? 'ring-2 ring-[#002FA7]' : ''}`}>
                        <div className={`px-4 py-3 flex items-center justify-between ${ci === 0 ? 'bg-gradient-to-r from-[#002FA7] to-indigo-700 text-white' : 'bg-gradient-to-r from-slate-700 to-slate-800 text-white'}`}>
                          <div className="flex items-center gap-2">
                            <span className="text-lg font-bold">#{ci + 1}</span>
                            <PatternDisplay pattern={combo.pattern} />
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold">{combo.pred_score}</div>
                            <div className="text-[10px] text-white/60">Score</div>
                          </div>
                        </div>
                        <div className="p-4">
                          <div className="flex items-center justify-center gap-3 mb-3">
                            {allKeysW4.map((bk) => {
                              const bp = combo.balls[bk];
                              return (
                                <div key={bk} className="text-center">
                                  <div className="text-[9px] text-gray-400 mb-0.5">{ballLabels[bk]}</div>
                                  <div className={`inline-flex w-10 h-10 rounded-full items-center justify-center font-mono font-bold text-sm ${ci === 0 ? 'bg-[#002FA7] text-white' : 'bg-slate-700 text-white'}`}>{bp.top_pick}</div>
                                  <div className={`text-[9px] font-mono font-bold mt-0.5 ${bp.direction === 'UP' ? 'text-emerald-600' : bp.direction === 'DOWN' ? 'text-red-500' : 'text-gray-400'}`}>
                                    {bp.direction === 'UP' ? '\u25B2' : bp.direction === 'DOWN' ? '\u25BC' : '='} {bp.picks?.[0]?.diff_label || '0'}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <div className="flex items-center justify-center gap-4 text-[10px] text-gray-500">
                            <span>Rank #{combo.rank}</span>
                            <span>{combo.times}x ({(combo.percentage * 100).toFixed(2)}%)</span>
                            <span>Gap: {combo.gap}</span>
                            <span className={combo.overdue_ratio > 1 ? 'text-red-600 font-bold' : ''}>{combo.overdue_ratio}x overdue</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Detail breakdown */}
                  {data.top4_predictions.map((combo, ci) => (
                    <div key={combo.pattern} className="bg-white border rounded-lg overflow-hidden">
                      <div className="bg-gray-100 px-4 py-2 flex items-center gap-2">
                        <span className="font-bold text-sm text-gray-700">#{ci + 1} Detail:</span>
                        <PatternDisplay pattern={combo.pattern} />
                        <span className="text-xs text-gray-500 ml-auto">Score: {combo.pred_score} | Overdue: {combo.overdue_ratio}x</span>
                      </div>
                      <div className="p-4">
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                          {allKeysW4.map((bk) => {
                            const bp = combo.balls[bk];
                            return (
                              <div key={bk} className="border rounded-lg overflow-hidden">
                                <div className={`px-2 py-1.5 text-white text-center text-xs font-bold ${bp.direction === 'UP' ? 'bg-emerald-600' : bp.direction === 'DOWN' ? 'bg-red-500' : 'bg-gray-500'}`}>
                                  {ballLabels[bk]} {bp.direction === 'UP' ? '\u25B2' : bp.direction === 'DOWN' ? '\u25BC' : '='} (last: {bp.last_value})
                                </div>
                                <div className="divide-y">
                                  {(bp.picks || []).slice(0, 4).map((pick, pi) => (
                                    <div key={pick.diff} className={`px-2 py-1.5 flex items-center justify-between ${pi === 0 ? 'bg-blue-50' : ''}`}>
                                      <div className="flex items-center gap-1">
                                        <span className={`font-mono font-bold text-sm ${pick.diff > 0 ? 'text-emerald-600' : pick.diff < 0 ? 'text-red-500' : 'text-gray-400'}`}>{pick.diff_label}</span>
                                        <span className="inline-flex w-6 h-6 rounded-full bg-gray-700 text-white items-center justify-center font-mono font-bold text-[9px]">{pick.target}</span>
                                      </div>
                                      <span className="text-[10px] text-gray-500 font-mono">{pick.count}x</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </>
        ) : null}
      </main>
    </div>
  );
}
